import { useState } from "react";
import RandomQuote from "../../components/RandomQuote/RandomQuote";
import Notification from "../../components/Notification/Notification";
import Spinner from "../../components/Spinner/Spinner";
import BrandTitle from "../../components/BrandTitle/BrandTitle";
import { Trans, useTranslation } from "react-i18next";

export default function EnterpriseSignupPage() {
  const { t, i18n } = useTranslation();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    email: "",
    firstName: "",
    lastName: "",
    password: "",
  });
  const [confirmPassword, setConfirmPassword] = useState("");
  const [billingContact, setBillingContact] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
  });

  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [notification, setNotification] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleBillingChange = (e) => {
    setBillingContact({ ...billingContact, [e.target.name]: e.target.value });
  };

  const goToBillingInfo = (e) => {
    e.preventDefault();
    setError("");
    if (!form.firstName || !form.lastName || !form.email || !form.password) {
      setError(t("auth.fillRequiredFields"));
      return;
    }
    if (form.password !== confirmPassword) {
      setError(t("auth.passwordsDoNotMatch"));
      return;
    }
    // Pre-fill billing contact email with user email
    if (!billingContact.email) {
      setBillingContact({ ...billingContact, email: form.email });
    }
    setStep(2);
  };

  const handleBack = (e) => {
    e.preventDefault();
    setStep(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");

    if (!termsAccepted) {
      setError(t("auth.acceptTerms"));
      return;
    }

    if (
      !billingContact.name ||
      !billingContact.email ||
      !billingContact.company
    ) {
      setError(t("auth.enterpriseSignup.fillBillingFields"));
      return;
    }

    setIsLoading(true);

    try {
      const payload = {
        ...form,
        language: i18n.resolvedLanguage,
        billingContact,
      };

      const res = await fetch(
        `${import.meta.env.VITE_API_URL}/api/auth/signup-enterprise`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t("auth.signupFailed"));

      // Stores the translation key so it follows language changes
      setSuccessMessage("auth.enterpriseSignup.accountCreated");
      setNotification({
        message: t("auth.enterpriseSignup.accountCreatedShort"),
        type: "success",
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="authentication-view">
      {isLoading && <Spinner text={t("auth.enterpriseSignup.creating")} />}
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
      <div className="left-wrapper">
        <div className="title">
          <BrandTitle fontSize={40} className="brand-title-login" />
        </div>
        <div className="quote">
          <RandomQuote />
        </div>
      </div>

      <div className="right-wrapper">
        {successMessage ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              padding: "40px",
              maxWidth: "500px",
              height: "100%",
              margin: "auto",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "15px",
                marginBottom: "30px",
              }}
            >
              <div
                style={{
                  fontSize: "48px",
                  color: "#6c5ce7",
                }}
              >
                ✓
              </div>
              <div
                className="title"
                style={{
                  margin: 0,
                  color: "#6c5ce7",
                }}
              >
                {t("auth.success")}
              </div>
            </div>
            <div
              style={{
                display: "inline-block",
                padding: "6px 12px",
                background: "rgba(108, 92, 231, 0.1)",
                color: "#6c5ce7",
                borderRadius: "6px",
                fontSize: "12px",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                marginBottom: "20px",
              }}
            >
              🏢 {t("auth.enterpriseSignup.accountBadge")}
            </div>
            <p
              style={{
                fontSize: "16px",
                lineHeight: "1.6",
                color: "#ccc",
                marginBottom: "30px",
              }}
            >
              {t(successMessage)}
            </p>
            <p>{t("auth.closeWindow")}</p>

            <p style={{ marginTop: "40px" }}>
              <a className="link" href="/login">
                {t("auth.enterpriseSignup.goToLogin")}
              </a>
            </p>
          </div>
        ) : (
          <>
            <div className="title">
              <span
                style={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                <span>{t("auth.enterpriseSignup.title")}</span>
                <span
                  style={{
                    fontSize: "12px",
                    fontWeight: 600,
                    padding: "4px 8px",
                    background: "rgba(108, 92, 231, 0.2)",
                    color: "#6c5ce7",
                    borderRadius: "4px",
                  }}
                >
                  {t("auth.enterpriseSignup.invoiceBillingBadge")}
                </span>
              </span>
            </div>

            {step === 1 && (
              <form onSubmit={goToBillingInfo} autoComplete="off">
                <div className="auth-names">
                  <input
                    name="firstName"
                    className="flow-form-row"
                    value={form.firstName}
                    onChange={handleChange}
                    placeholder={t("auth.firstName")}
                    autoComplete="new-first-name"
                    required
                  />
                  <input
                    name="lastName"
                    className="flow-form-row"
                    value={form.lastName}
                    onChange={handleChange}
                    placeholder={t("auth.lastName")}
                    autoComplete="new-last-name"
                    required
                  />
                </div>
                <br />
                <input
                  name="email"
                  className="flow-form"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder={t("auth.enterpriseSignup.yourEmail")}
                  autoComplete="new-email"
                  required
                />
                <br />
                <input
                  name="password"
                  className="flow-form"
                  type="password"
                  value={form.password}
                  onChange={handleChange}
                  placeholder={t("common.password")}
                  autoComplete="new-password"
                  required
                />
                <br />
                <input
                  name="confirmPassword"
                  className="flow-form"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={t("auth.confirmPassword")}
                  autoComplete="new-confirm-password"
                  required
                />
                <br />
                {error && (
                  <span
                    style={{
                      color: "var(--error-color)",
                      fontSize: "14px",
                      display: "block",
                      marginBottom: "10px",
                    }}
                  >
                    {error}
                  </span>
                )}
                <button type="submit" className="flow-button">
                  {t("auth.signup.nextBillingContact")}
                </button>
              </form>
            )}

            {step === 2 && (
              <form onSubmit={handleSubmit} autoComplete="off">
                <div
                  style={{
                    marginBottom: "20px",
                    fontSize: "14px",
                    color: "#999",
                  }}
                >
                  <p style={{ margin: "0 0 10px 0" }}>
                    <strong>
                      {t("auth.enterpriseSignup.billingInfoTitle")}
                    </strong>
                  </p>
                  <p style={{ margin: 0 }}>
                    {t("auth.enterpriseSignup.billingInfoText")}
                  </p>
                </div>

                <input
                  name="company"
                  className="flow-form"
                  value={billingContact.company}
                  onChange={handleBillingChange}
                  placeholder={t("auth.enterpriseSignup.company")}
                  required
                />
                <br />
                <input
                  name="name"
                  className="flow-form"
                  value={billingContact.name}
                  onChange={handleBillingChange}
                  placeholder={t("auth.enterpriseSignup.contactName")}
                  required
                />
                <br />
                <input
                  name="email"
                  className="flow-form"
                  type="email"
                  value={billingContact.email}
                  onChange={handleBillingChange}
                  placeholder={t("auth.enterpriseSignup.contactEmail")}
                  required
                />
                <br />
                <input
                  name="phone"
                  className="flow-form"
                  type="tel"
                  value={billingContact.phone}
                  onChange={handleBillingChange}
                  placeholder={t("auth.enterpriseSignup.contactPhone")}
                />
                <br />

                {/* Terms and conditions */}
                <div
                  style={{
                    marginTop: "24px",
                    marginBottom: "20px",
                    fontSize: "14px",
                    lineHeight: "1.6",
                    color: "#999",
                  }}
                >
                  <p style={{ margin: "0 0 16px 0" }}>
                    <Trans
                      i18nKey="auth.enterpriseSignup.billingTerms"
                      components={{ bold: <strong /> }}
                    />
                  </p>

                  <label
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      cursor: "pointer",
                      gap: "10px",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                      style={{
                        marginTop: "3px",
                        cursor: "pointer",
                        width: "16px",
                        height: "16px",
                        flexShrink: 0,
                      }}
                    />
                    <span>
                      <Trans
                        i18nKey="auth.enterpriseSignup.agreeTerms"
                        components={{
                          terms: (
                            <a
                              href="/legal/terms-of-service"
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: "#6c5ce7",
                                textDecoration: "underline",
                              }}
                            />
                          ),
                          privacy: (
                            <a
                              href="/legal/privacy-policy"
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: "#6c5ce7",
                                textDecoration: "underline",
                              }}
                            />
                          ),
                        }}
                      />
                    </span>
                  </label>
                </div>

                {error && (
                  <span
                    style={{
                      color: "var(--error-color)",
                      fontSize: "14px",
                      display: "block",
                      marginBottom: "10px",
                    }}
                  >
                    {error}
                  </span>
                )}

                <div style={{ display: "flex", gap: "10px" }}>
                  <button
                    type="button"
                    onClick={handleBack}
                    className="flow-button"
                    style={{
                      background: "transparent",
                      border: "1px solid var(--border-color)",
                      color: "var(--main-text-color)",
                    }}
                  >
                    {t("common.back")}
                  </button>
                  <button
                    type="submit"
                    className="flow-button"
                    style={{ flex: 1, background: "#6c5ce7" }}
                  >
                    {t("auth.enterpriseSignup.submit")}
                  </button>
                </div>
              </form>
            )}

            {!successMessage && (
              <p style={{ marginTop: "20px" }}>
                {t("auth.enterpriseSignup.needRegular")}{" "}
                <a className="link" href="/signup">
                  {t("auth.enterpriseSignup.signUpHere")}
                </a>
              </p>
            )}
          </>
        )}

        <div className="logo">
          <img
            src="/somigo_logo.svg"
            alt={t("auth.logoAlt")}
            width="100"
            height="100"
          />
        </div>
      </div>
    </div>
  );
}
